import { shallowRef, watch } from 'vue-demi';

/**
 * Acts as decorator for a list or item loader.
 *
 * When the loader state is reset, usually as the result of changing request parameters,
 * then the previous loader state is maintained for up to 500ms by default.
 * This helps to reduce flickering in the UI.
 * @param state { object } - the output of a list or item loader, e.g. const state = useProjectsLoader({...})
 * @param delayTimeMs { number} - how long should wait before updating the state object in milliseconds. default is 500ms
 */
export default function useDelayLoader(state, delayTimeMs = 500) {
  // `state.items.value` is an Array for a list loader state and undefined for an item loader state.
  const isList = Boolean(state.items.value);
  // A handle used for tracking the delayed state updates.
  let timeout;

  // A shallow copy of the data from `state`.
  // We'll keep it in sync with `state` using `watch` but
  // delay the update when transitioning from a non-loading to a loading state.
  const items = isList ? shallowRef(state.items.value) : undefined;
  const totalCount = isList ? shallowRef(state.totalCount.value) : undefined;
  const itemInSync = state.itemInSync;
  const item = !isList ? shallowRef(state.item.value) : undefined;
  const inSync = shallowRef(state.inSync.value);
  const loading = shallowRef(state.loading.value);
  const meta = shallowRef(state.meta.value);
  const response = shallowRef(state.response.value);
  const error = shallowRef(state.error.value);

  function updateState() {
    clearTimeout(timeout);
    if (isList) {
      items.value = state.items.value;
      totalCount.value = state.totalCount.value;
    } else {
      item.value = state.item.value;
    }
    inSync.value = state.inSync.value;
    loading.value = state.loading.value;
    meta.value = state.meta.value;
    response.value = state.response.value;
    error.value = state.error.value;
  }

  function isLoadingNow() {
    const hasSuccess = isList ? totalCount.value !== undefined : item.value !== undefined;
    const hasError = error.value && (!error.value.config || error.value.config.cache !== 'only-if-cached');

    return !hasSuccess && !hasError;
  }

  function isLoadingNext() {
    const hasSuccess = isList ? state.totalCount.value !== undefined : state.item.value !== undefined;
    const hasError =
      state.error.value && (!state.error.value.config || state.error.value.config.cache !== 'only-if-cached');

    return !hasSuccess && !hasError;
  }

  const watchList = [
    isList && state.items,
    isList && state.totalCount,
    !isList && state.item,
    state.inSync,
    state.loading,
    state.meta,
    state.response,
    state.error,
  ].filter(Boolean);

  watch(watchList, () => {
    if (!isLoadingNow() && isLoadingNext()) {
      timeout = setTimeout(updateState, delayTimeMs);
    } else {
      updateState();
    }
  });

  return {
    items,
    totalCount,
    itemInSync,
    item,
    inSync,
    loading,
    meta,
    response,
    error,
  };
}
